// Attribution that follows a funnel buyer from the front-end purchase onto
// every later money row: the upsell and downsell ledger rows, the Stripe
// subscription or PayPal merchant-managed subscription they create, and each
// trial conversion and renewal of that subscription. The spend-to-revenue
// view joins on these keys, so a renewal a year later still counts toward the
// campaign that bought the customer.
//
// Stored subscription metadata (Stripe, PayPal merchant-managed) uses the
// checkout metadata names (attr_*). Ledger rows use the event_data names
// (utm_*, meta_*, attr_fbc, attr_fbp, attr_fbclid).
//
// The first-touch landing URL travels as `carried_landing_url`, never as
// attr_source_url: the commerce_ledger trigger set_commerce_ledger_source_path
// and the admin funnel SQL read attr_source_url (and source_path, sourceUrl,
// checkout_source_path) as the sale's funnel page, so a renewal carrying it
// would count as a new sale on the landing step.
//
// No imports, so Vitest and Deno can both load it.

/** [checkout metadata key, ledger / event_data key] for each carried field. */
export const CARRIED_ATTRIBUTION_KEYS: ReadonlyArray<readonly [string, string]> = [
  ['attr_utm_source', 'utm_source'],
  ['attr_utm_medium', 'utm_medium'],
  ['attr_utm_campaign', 'utm_campaign'],
  ['attr_utm_term', 'utm_term'],
  ['attr_utm_content', 'utm_content'],
  ['attr_utm_id', 'utm_id'],
  ['attr_meta_campaign_id', 'meta_campaign_id'],
  ['attr_meta_adset_id', 'meta_adset_id'],
  ['attr_meta_ad_id', 'meta_ad_id'],
  ['attr_fbclid', 'attr_fbclid'],
  ['attr_fbc', 'attr_fbc'],
  ['attr_fbp', 'attr_fbp'],
  ['carried_landing_url', 'carried_landing_url'],
];

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function recordOrEmpty(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

/** The carried keys of checkout-style metadata (attr_*), values unchanged. */
export function pickCarriedAttributionMetadata(metadata: unknown): Record<string, string> {
  const source = recordOrEmpty(metadata);
  const picked: Record<string, string> = {};
  for (const [metadataKey] of CARRIED_ATTRIBUTION_KEYS) {
    const value = stringOrNull(source[metadataKey]);
    if (value) picked[metadataKey] = value;
  }
  return picked;
}

/**
 * The carried fields of stored subscription metadata, under the ledger's
 * event_data names. Reads the checkout metadata names (attr_utm_campaign) and
 * falls back to the event_data names (utm_campaign), so either kind of stored
 * record works.
 */
export function carriedAttributionEventData(metadata: unknown): Record<string, string> {
  const source = recordOrEmpty(metadata);
  const eventData: Record<string, string> = {};
  for (const [metadataKey, eventDataKey] of CARRIED_ATTRIBUTION_KEYS) {
    const value = stringOrNull(source[metadataKey]) ?? stringOrNull(source[eventDataKey]);
    if (value) eventData[eventDataKey] = value;
  }
  return eventData;
}

/**
 * Meta campaign, ad set and ad ids are all-digit strings. Anything else (an
 * unresolved `{{campaign.id}}` macro, a campaign name) is dropped.
 */
export function normalizeMetaAdObjectId(value: unknown): string | null {
  const normalized = stringOrNull(value);
  return normalized && /^\d{5,30}$/.test(normalized) ? normalized : null;
}

// Stripe rejects metadata with more than 50 keys, which would fail the
// charge. Attribution keys that repeat another key go first. Google's iOS
// click ids (wbraid, gbraid) go last: on many iPhone visits they are the only
// Google Ads click identifier, while the page URL, GA ids, user agent and IP
// can come from the request or other keys (same rule as the 30-key list in
// conversionAttribution.ts).
export const STRIPE_METADATA_MAX_KEYS = 50;
const STRIPE_METADATA_DROP_ORDER = [
  'attr_acq_keyword',
  'attr_acq_creative',
  'attr_acq_ad',
  'attr_acq_ad_group',
  'attr_acq_campaign',
  'attr_acq_medium',
  'attr_acq_source',
  'attr_acq_network',
  'attr_ga_session_id',
  'attr_ga_client_id',
  'attr_appsflyer_web_user_id',
  'attr_page_url',
  'attr_user_agent',
  'attr_client_ip',
  'attr_wbraid',
  'attr_gbraid',
];

/** Drops low-value attribution keys until the metadata fits Stripe's limit. */
export function limitStripeMetadataKeys<T extends Record<string, string>>(
  metadata: T,
  maxKeys: number = STRIPE_METADATA_MAX_KEYS
): T {
  if (Object.keys(metadata).length <= maxKeys) return metadata;
  const next: Record<string, string> = { ...metadata };
  for (const key of STRIPE_METADATA_DROP_ORDER) {
    if (Object.keys(next).length <= maxKeys) break;
    delete next[key];
  }
  return next as T;
}

const SUBSCRIPTION_PURCHASE_EVENT_PREFIX = 'subscription_purchase:';

/**
 * Meta event id for the Purchase copy of a funnel upsell. The upsell server
 * send goes to Meta twice: as Subscribe under its subscription_purchase id,
 * and as Purchase under this id, so Meta keeps both events. The Purchase is
 * server-only: the confirmation pages trust their query string and never
 * fire a browser copy of it.
 */
export function funnelUpsellMetaPurchaseEventId(subscriptionEventId: string): string {
  const trimmed = subscriptionEventId.trim();
  const rest = trimmed.startsWith(SUBSCRIPTION_PURCHASE_EVENT_PREFIX)
    ? trimmed.slice(SUBSCRIPTION_PURCHASE_EVENT_PREFIX.length)
    : trimmed;
  return `upsell_purchase:${rest}`;
}
